/**
 * Usage without sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w" />
 *
 * Usage with sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *     sizes="(max-width: 600px) 100vw, 50vw"
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w"
 *     sizes="(max-width: 600px) 100vw, 50vw" />
 *
 *   // This means that below 600px image will take as many pixels there are available on current
 *   // viewport width (100vw) - and above that image will only take 50% of the page width.
 *   // Browser decides which image it will fetch based on current screen size.
 *
 * NOTE: for all the possible image variant names and their respective
 * sizes, see the API documentation.
 */

import React, { useState } from 'react';
import { arrayOf, oneOfType, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  isRewritten,
  originImageUrl,
  reportImageCdnFallback,
  rewriteImageUrl,
} from '../../util/imageHost';

import NoImageIcon from './NoImageIcon';
import css from './ResponsiveImage.module.css';

const ResponsiveImage = props => {
  const {
    className,
    rootClassName,
    alt,
    noImageMessage,
    image,
    variants,
    dimensions,
    loading = 'lazy',
    fetchpriority,
    ...rest
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  // Si nuestro CDN de imágenes falla (cupo agotado, caída, un bug nuestro), esta
  // imagen y las siguientes vuelven a pedirse a Sharetribe. Degradar a "las fotos
  // van por donde iban antes" es mucho mejor que quedarse sin fotos.
  const [cdnFailed, setCdnFailed] = useState(false);

  if (image == null || variants.length === 0) {
    const noImageClasses = classNames(rootClassName || css.root, css.noImageContainer, className);

    const noImageMessageText = noImageMessage || <FormattedMessage id="ResponsiveImage.noImage" />;
    return (
      <div className={noImageClasses}>
        <div className={css.noImageWrapper}>
          <NoImageIcon className={css.noImageIcon} />
          <div className={css.noImageText}>{noImageMessageText}</div>
        </div>
      </div>
    );
  }

  const imageVariants = image.attributes.variants;

  const srcSet = variants
    .map(variantName => {
      const variant = imageVariants[variantName];

      if (!variant) {
        // Variant not available (most like just not loaded yet)
        return null;
      }
      // `variant.url` ya viene por nuestro host (se reescribe al entrar al almacén), así que
      // el repliegue tiene que DESHACERLO, no dejarla como está. `rewriteImageUrl` sigue
      // porque hay imágenes que no pasan por el almacén (los assets de `Page.js`).
      const url = cdnFailed ? originImageUrl(variant.url) : rewriteImageUrl(variant.url);
      return `${url} ${variant.width}w`;
    })
    .filter(v => v != null)
    .join(', ');

  const handleError = e => {
    // Solo repliega si lo que ha fallado es NUESTRO host. Si falla también el
    // original, `cdnFailed` ya está puesto y aquí no se hace nada: sin bucles.
    const failed = e?.target?.currentSrc || '';
    if (!cdnFailed && isRewritten(failed)) {
      setCdnFailed(true);
      reportImageCdnFallback(failed);
    }
  };

  const imgProps = {
    className: classes,
    srcSet,
    ...rest,
  };

  return (
    <img
      alt={alt}
      {...imgProps}
      loading={loading}
      fetchpriority={fetchpriority}
      decoding="async"
      onError={handleError}
    />
  );
};

ResponsiveImage.defaultProps = {
  className: null,
  rootClassName: null,
  image: null,
  sizes: null,
  noImageMessage: null,
};

ResponsiveImage.propTypes = {
  className: string,
  rootClassName: string,
  alt: string.isRequired,
  image: oneOfType([propTypes.image, propTypes.imageAsset]),
  variants: arrayOf(string).isRequired,
  sizes: string,
  noImageMessage: string,
  // mobile/LCP tuning
  loading: string,
  fetchpriority: string,
};

export default ResponsiveImage;
